import {
    Alert,
    Button,
    Checkbox,
    Radio,
    CircularProgress,
    Divider,
    FormControlLabel,
    Grid,
    IconButton,
    Stack,
    Step,
    StepButton,
    StepLabel,
    Stepper,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { LoadingButton } from "@mui/lab";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNotification } from "providers/Notification";
import { useLogin } from "providers/Login";
import {
    AzureDirectoryConfig,
    Directory,
    DirectoryConfigInput,
    DirectoryType,
    DomainControllerDiscovery,
    GoogleDirectoryConfig,
    LdapDirectoryConfig,
    LdapDomainController,
    OktaDirectoryConfig,
    canManageGroups as canManageDirectoryGroups,
    controllerEndpoint,
    directoryDisplayNames,
    maskedSecret,
} from "./_components/Types";
import { directoryIcons } from "./_components/BrandIcons";
import * as directoriesApi from "./_components/directoriesApi";
import { ShieldInstancesSelect } from "components/ShieldInstancesSelect";
import StatusLabel from "components/StatusLabel";

type WizardStep = "setup" | "consent" | "domains";

const stepDisplayNames: { [key in WizardStep]: string } = {
    "setup": "Setup",
    "consent": "Authorization",
    "domains": "Domains",
};

const connectionHelp: { [key in DirectoryType]: string } = {
    "google": "Register the redirect URI below in the Google console, then download credentials.json, upload it and grant consent.",
    "azure": "Register our application in your Microsoft Entra admin center and grant it the \"Directory.Read.All\" and \"User.Read.All\" API permissions as an Application.",
    "okta": "Generate an API token in your Okta admin dashboard.",
    "ldap": "Point us at a domain controller and a read-only bind account.",
    // Never actually shown - the Proxy Directory is never created or edited
    // through this wizard, only system-seeded.
    "proxy": "",
};

// Normalises an LDAP host into `scheme://host:port`. Only for immediate feedback in the
// field - activedirectory.NormalizeAddr does the same on the way in, and is what actually
// decides what gets dialled.
function normalizeLdapHost(raw: string): string {
    const value = raw.trim();
    if (value === "") {
        return value;
    }

    let url: URL;
    try {
        url = new URL(/^ldaps?:\/\//i.test(value) ? value : `ldap://${value}`);
    } catch {
        return value;
    }

    if (url.protocol !== "ldap:" && url.protocol !== "ldaps:") {
        return value;
    }
    if (!url.port) {
        url.port = url.protocol === "ldaps:" ? "636" : "389";
    }
    return `${url.protocol.slice(0, -1)}://${url.host}`;
}

interface CredentialField {
    key: string
    id: string
    label: string
    secret?: boolean
    required?: boolean
    normalize?: (value: string) => string
}

function fieldsFor<C>(fields: (CredentialField & { key: keyof C & string })[]): CredentialField[] {
    return fields;
}

const credentialFields: { [key in DirectoryType]: CredentialField[] } = {
    "google": [],
    "azure": fieldsFor<AzureDirectoryConfig>([
        { key: "tenantId", id: "azure-tenant-id-input", label: "Directory (tenant) ID", required: true },
        { key: "clientId", id: "azure-client-id-input", label: "Application (client) ID", required: true },
        { key: "clientSecret", id: "azure-client-secret-input", label: "Client Secret Value", required: true, secret: true },
    ]),
    "okta": fieldsFor<OktaDirectoryConfig>([
        { key: "apiDomain", id: "okta-api-domain-input", label: "Okta API Domain", required: true },
        { key: "apiToken", id: "okta-api-token-input", label: "API Token", required: true, secret: true },
    ]),
    "ldap": fieldsFor<LdapDirectoryConfig>([
        { key: "addr", id: "ldap_host_input", label: "Host (hostname:port)", required: true, normalize: normalizeLdapHost },
        { key: "username", id: "ldap_username_input", label: "Username", required: true },
        { key: "password", id: "ldap_password_input", label: "Password", required: true, secret: true },
    ]),
    // Never actually used - see connectionHelp above.
    "proxy": [],
};

interface DomainDiscovery {
    addr: string
    domains: string[]
    failed?: boolean
}

function includesDomain(domains: string[], domain: string): boolean {
    return domains.some((d) => d.toLowerCase() === domain.toLowerCase());
}

// A domain controller endpoint, written out in full: protocol://host:port. The
// host may be a name, an IPv4 address, or a bracketed IPv6 literal.
const controllerEndpointPattern = /^ldaps?:\/\/(?:\[[0-9A-Fa-f:]+\]|[A-Za-z0-9._-]+):\d{1,5}$/i;

// controllerEndpointError is deliberately STRICTER than the backend's
// activedirectory.ValidateEndpoint, which also takes a bare host and infers the
// forest's scheme and port for it. Requiring all three parts here means what an
// admin typed is what gets dialled, with nothing inferred behind their back -
// which matters most for the scheme, where an inherited ldap:// is cleartext.
//
// Being stricter is the safe direction: everything this accepts, the backend
// accepts too, so no value can pass the field and then fail the save. Loosening it
// is what would need care.
//
// A domain is only ever reached through its controller, so empty is an error.
function controllerEndpointError(value: string): string {
    const trimmed = value.trim();
    if (trimmed === "") {
        return "Required.";
    }
    if (/\s/.test(trimmed)) {
        return "Cannot contain spaces.";
    }
    if (!/:\/\//.test(trimmed)) {
        return "Include the protocol, for example ldap://10.1.1.101:389.";
    }
    if (!/^ldaps?:\/\//i.test(trimmed)) {
        return "Must use ldap:// or ldaps://.";
    }
    if (!/:\d{1,5}$/.test(trimmed)) {
        return "Include the port, for example ldap://10.1.1.101:389.";
    }
    if (!controllerEndpointPattern.test(trimmed)) {
        return "Use protocol://host:port, for example ldaps://dc01.example.com:636.";
    }
    const port = Number(trimmed.slice(trimmed.lastIndexOf(":") + 1));
    if (port < 1 || port > 65535) {
        return "Port must be between 1 and 65535.";
    }
    return "";
}

function sameHost(a: string, b: string): boolean {
    return a.toLowerCase() === b.toLowerCase();
}

// Everything the wizard collects, flat and type-agnostic
interface DirectoryDraft {
    name: string
    description: string
    credentials: { [key: string]: string }
    google: GoogleDirectoryConfig
    domains: string[]
    // Only ever set on an LDAP forest domain row. Order matters: it is the order
    // the endpoints are tried, so it has to survive editing intact.
    domainController: string
    icapInstances: string[]
    enabled: boolean
}

function initialDraft(type: DirectoryType, directory: Directory | undefined, children: Directory[]): DirectoryDraft {
    // The Proxy Directory has no config block, but this wizard never runs
    // against it (never offered for Edit, never creatable through here).
    const stored = ((directory && directory.type !== "proxy" ? directory.config : undefined) || {}) as { [key: string]: string | undefined };
    const credentials: { [key: string]: string } = {};
    for (const field of credentialFields[type]) {
        credentials[field.key] = field.secret
            ? (stored[field.key] ? maskedSecret : "")
            : (stored[field.key] || "");
    }

    return {
        name: directory?.name || "",
        description: directory?.description || "",
        credentials,
        google: directory?.type === "google" ? directory.config : { hasCredential: false, hasToken: false },
        // Forest domain children are always ldap - the Proxy Directory has no
        // children mechanism at all - but Directory's type doesn't say so.
        domains: children.map((c) => (c as { config?: LdapDirectoryConfig }).config?.domain || "").filter((d) => d !== ""),
        domainController: directory?.type === "ldap" ? (directory.config.domainController || "") : "",
        icapInstances: directory?.icapInstances || [],
        enabled: directory?.domainEnabled ?? directory?.enabled ?? true,
    };
}

function secretToSubmit(value: string | undefined): string | undefined {
    return value === undefined || value === "" || value === maskedSecret ? undefined : value;
}

function providerError(err: unknown, fallback: string): string {
    if (!axios.isAxiosError(err) || err.response === undefined) {
        return fallback;
    }
    if (err.response.status < 400 || err.response.status >= 500) {
        return fallback;
    }
    const message = (err.response.data as { message?: string } | undefined)?.message;
    return message ? String(message) : fallback;
}

function buildConfig(type: DirectoryType, draft: DirectoryDraft): DirectoryConfigInput {
    if (type === "google") {
        return draft.google;
    }
    const submitted = credentialFields[type].map((field) => {
        const value = draft.credentials[field.key] || "";
        if (field.secret) {
            return [field.key, secretToSubmit(value)];
        }
        return [field.key, field.normalize ? field.normalize(value) : value.trim()];
    });
    return Object.fromEntries(submitted.filter(([, value]) => value !== undefined)) as DirectoryConfigInput;
}

function requiredFieldsFilled(type: DirectoryType, draft: DirectoryDraft): boolean {
    return credentialFields[type].every(
        (f) => !f.required || (draft.credentials[f.key] || "").trim() !== "");
}

function canEnable(type: DirectoryType, draft: DirectoryDraft): boolean {
    return type !== "google"
        || (draft.google.hasToken && draft.google.pendingCredentialsName === undefined);
}

// A second screen is added only where the work is genuinely ordered - Google cannot consent
// before its credentials are uploaded, and a forest cannot list its domains before it can
// bind. Editing an LDAP directory is neither: its credentials are already stored, so the
// domains they reach belong on the same screen
function stepsFor(type: DirectoryType, isChildDomain: boolean, isEdit: boolean): WizardStep[] {
    if (isChildDomain) {
        return ["setup"];
    }
    switch (type) {
        case "google":
            return ["setup", "consent"];
        case "ldap":
            return isEdit ? ["setup"] : ["setup", "domains"];
        default:
            return ["setup"];
    }
}

function isStepComplete(
    step: WizardStep,
    type: DirectoryType,
    draft: DirectoryDraft,
    isChildDomain: boolean,
): boolean {
    if (step === "setup") {
        return draft.name.trim() !== "" && (isChildDomain || requiredFieldsFilled(type, draft));
    }
    if (step === "consent") {
        return draft.google.hasCredential || draft.google.pendingCredentialsName !== undefined;
    }
    return true;
}

// Users are only looked up in an LDAP directory's selected domains, so one with
// none selected would never find anyone.
function hasRequiredDomains(type: DirectoryType, draft: DirectoryDraft, isChildDomain: boolean): boolean {
    return type !== "ldap" || isChildDomain || draft.domains.length > 0;
}

interface DirectoryWizardProps {
    type: DirectoryType
    directory?: Directory
    childDirectories?: Directory[]
    googleSetup?: directoriesApi.GoogleDirectorySetup
    initialStep?: WizardStep
    onExit: () => void
    onSaved: () => void
    onManageGroups?: (id: string) => void
}

// Add / Edit flow for a directory integration. Steps depend on the directory type and
// on whether the row is a child domain, which inherits its credentials from the forest parent.
// In edit mode every step is reachable directly and Save is always available, so it reads as a
// prefilled form rather than a march through a checklist.
export function DirectoryWizard(props: DirectoryWizardProps) {
    const { type, directory, googleSetup } = props;
    const notification = useNotification();
    const login = useLogin();
    const req = login.GetAxios();
    const isEdit = directory !== undefined;
    const isChildDomain = directory?.parentId !== undefined;
    const existingChildren = props.childDirectories || [];
    const steps = stepsFor(type, isChildDomain, isEdit);
    const isSinglePage = steps.length === 1;
    const [stepIndex, setStepIndex] = useState(() => {
        const requested = props.initialStep ? steps.indexOf(props.initialStep) : -1;
        return requested === -1 ? 0 : requested;
    });
    const [draft, setDraft] = useState(() => initialDraft(type, directory, existingChildren));
    // An LDAP directory is looked up domain by domain, each selected domain its own row.
    const managesDomains = type === "ldap" && !isChildDomain;
    // A stored row must qualify the same way the table's row action does; a new
    // LDAP directory's groups live on the domain rows it is about to create.
    const canManageGroups = (directory === undefined || canManageDirectoryGroups(directory))
        && (type !== "ldap" || isChildDomain);
    const [busy, setBusy] = useState<"" | "upload" | "consent" | "domains" | "controllers" | "discard" | "save" | "test">("");
    const [testResult, setTestResult] = useState<{ ok: boolean, message: string } | undefined>();
    const [directoryId, setDirectoryId] = useState(directory?.id);
    const [discovery, setDiscovery] = useState<DomainDiscovery | undefined>();
    // Domain controllers are only ever listed for a forest domain row, which is the
    // only row that reads its groups through one specific domain's controller.
    const showsControllers = type === "ldap" && isChildDomain;
    const [controllers, setControllers] = useState<DomainControllerDiscovery | undefined>();
    const [controllersFailed, setControllersFailed] = useState(false);
    const [customController, setCustomController] = useState(false);
    const [storedAddr] = useState(() => normalizeLdapHost(
        (directory?.type === "ldap" ? directory.config.addr : "") || ""));
    const [callbackUrlCopied, setCallbackUrlCopied] = useState(false);
    const currentStep = steps[stepIndex];
    const isLastStep = stepIndex === steps.length - 1;
    const enabledStep: WizardStep = isEdit ? "setup" : steps[steps.length - 1];
    // Only a value the admin typed into the custom field is held to the full
    // protocol://host:port form. One from the discovered list, or the stored one -
    // picked at setup, so possibly a bare address - is what the directory reported
    // and not the admin's to get wrong.
    const storedController = directory?.type === "ldap" ? (directory.config.domainController || "") : "";
    const controllerError = customController && draft.domainController !== storedController
        ? controllerEndpointError(draft.domainController) : "";
    const canSave = steps.every((step) => isStepComplete(step, type, draft, isChildDomain))
        && hasRequiredDomains(type, draft, isChildDomain)
        && (!showsControllers || draft.domainController.trim() !== "")
        && controllerError === "";
    const domainsInSetup = type === "ldap" && isEdit && !isChildDomain;
    const showsDomains = currentStep === "domains" || (domainsInSetup && currentStep === "setup");
    const hasAnyCredentials = draft.google.hasCredential || draft.google.pendingCredentialsName !== undefined;

    const patch = (update: Partial<DirectoryDraft>) => {
        setDraft((current) => ({ ...current, ...update }));
    };

    const patchGoogle = (update: Partial<GoogleDirectoryConfig>) => {
        setDraft((current) => ({ ...current, google: { ...current.google, ...update } }));
    };

    const patchCredential = (key: string, value: string) => {
        if ((draft.credentials[key] || "") === value) {
            return;
        }
        setTestResult(undefined);
        setDraft((current) => ({
            ...current,
            credentials: { ...current.credentials, [key]: value },
        }));
    };

    const patchCredentialOnBlur = (field: CredentialField, value: string) => {
        const normalized = field.normalize ? field.normalize(value) : value;
        patchCredential(field.key, normalized);

        if (field.key === "addr" && showsDomains && normalized !== ""
            && !sameHost(normalized, discovery?.addr || "")) {
            discoverDomains(normalized);
        }
    };

    const ensureDirectoryId = async (): Promise<string> => {
        if (directoryId) {
            return directoryId;
        }
        const [created] = await directoriesApi.saveDirectory(req!, {
            type,
            name: draft.name,
            description: draft.description,
            config: buildConfig(type, draft),
            onCreated: setDirectoryId,
        });
        return created.id;
    };

    const uploadGoogleCredentials = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const input = event.target;
        const file = input.files && input.files[0];
        if (!file || !req) {
            return;
        }
        setBusy("upload");
        try {
            // Staged, not applied: what this directory already syncs with is untouched until
            // Google calls our callback with a token for the new file.
            const { consentUrl, credentialsName } = await directoriesApi.integrateGoogle(
                req, await ensureDirectoryId(), file);
            patchGoogle({ pendingCredentialsName: credentialsName || file.name, consentUrl });
        } catch (err) {
            console.error(err);
            notification.Display({
                title: "Error",
                message: providerError(err, "Could not upload the credentials file."),
                type: "error",
            });
        }

        input.value = "";
        setBusy("");
    };

    // Backing out has to leave the directory as it was found, so the staged file is thrown
    // away - but only where there is a granted one to fall back on. On a directory that has
    // none, discarding would just cost the admin the upload they meant to make.
    const discardStagedUpload = async () => {
        if (!req || !directoryId || !draft.google.pendingCredentialsName || !draft.google.hasCredential) {
            return;
        }
        setBusy("discard");
        try {
            await directoriesApi.discardGoogleCredentials(req, directoryId);
            patchGoogle({ pendingCredentialsName: undefined, consentUrl: undefined });
        } catch (err) {
            console.error(err);
            notification.Display({
                title: "Error",
                message: providerError(err, "Could not discard the uploaded credentials file."),
                type: "error",
            });
        }
        setBusy("");
    };

    const testConnection = async () => {
        if (!req) {
            return;
        }
        setBusy("test");
        setTestResult(undefined);
        try {
            await directoriesApi.testDirectoryConnection(req, {
                id: directoryId,
                type,
                config: buildConfig(type, draft),
            });
            setTestResult({
                ok: true,
                message: type === "ldap"
                    ? "Connected and authenticated. The bind succeeded."
                    : "Connected and authenticated. The provider accepted these credentials.",
            });
        } catch (err) {
            console.error(err);
            const reason = providerError(err, "Could not reach the provider.");
            setDraft((current) => ({ ...current, enabled: false }));
            setTestResult({
                ok: false,
                message: `${reason} This integration cannot be enabled until a connection test succeeds.`,
            });
        }
        setBusy("");
    };

    const goBack = async () => {
        await discardStagedUpload();
        setStepIndex(stepIndex - 1);
    };

    const exit = async () => {
        await discardStagedUpload();
        props.onExit();
    };

    const copyCallbackUrl = () => {
        const callbackUrl = googleSetup?.callbackUrl;
        if (!callbackUrl) {
            return;
        }
        navigator.clipboard.writeText(callbackUrl).catch(console.error);
        setCallbackUrlCopied(true);
        setTimeout(() => setCallbackUrlCopied(false), 2000);
    };


    const discoverDomains = async (addr: string): Promise<DomainDiscovery | undefined> => {
        if (!req || addr === "") {
            return undefined;
        }
        let result: DomainDiscovery;
        setBusy("domains");
        try {
            const domains = await directoriesApi.discoverLdapDomains(req, {
                id: directoryId,
                addr,
                username: draft.credentials.username || "",
                password: secretToSubmit(draft.credentials.password),
            });
            result = { addr, domains };
            setDiscovery(result);

            if (!sameHost(addr, storedAddr)) {
                setDraft((current) => ({
                    ...current,
                    domains: current.domains.filter((d) => includesDomain(domains, d)),
                }));
            }
        } catch (err) {
            console.error(err);
            result = { addr, domains: [], failed: true };
            setDiscovery(result);
            notification.Display({
                title: "Error",
                message: providerError(err, "Could not list domains for this directory."),
                type: "error",
            });
        }
        setBusy("");
        return result;
    };

    useEffect(() => {
        const addr = normalizeLdapHost(draft.credentials.addr || "");
        if (showsDomains && addr !== "" && !sameHost(addr, discovery?.addr || "")) {
            discoverDomains(addr);
        }
    }, [showsDomains]); // eslint-disable-line react-hooks/exhaustive-deps

    const discoverDomainControllers = async () => {
        if (!req || !directoryId) {
            return;
        }
        setBusy("controllers");
        try {
            const found = await directoriesApi.discoverLdapDomainControllers(req, { id: directoryId });
            setControllers(found);
            setControllersFailed(false);
            // A stored pin that none of the discovered controllers matches was typed
            // by hand, so the custom option owns it. Deciding this only once the list
            // is in hand is the point: before that, every pin looks unmatched.
            setCustomController(draft.domainController !== ""
                && !(found.controllers || []).some((dc) => controllerMatches(dc, draft.domainController)));
        } catch (err) {
            console.error(err);
            // Nothing to match against, so a stored pin is the admin's own value.
            setCustomController(draft.domainController !== "");
            // The panel still renders what is stored, so the pin stays editable even
            // when nothing can be listed - which is precisely when an admin needs to
            // type a controller in by hand.
            setControllersFailed(true);
            notification.Display({
                title: "Error",
                message: providerError(err, "Could not list the domain controllers for this domain."),
                type: "error",
            });
        }
        setBusy("");
    };

    useEffect(() => {
        if (showsControllers) {
            discoverDomainControllers();
        }
    }, [showsControllers]); // eslint-disable-line react-hooks/exhaustive-deps

    const draftToSave = (): directoriesApi.SaveDirectoryInput => ({
        id: directoryId,
        type,
        name: draft.name.trim(),
        description: draft.description.trim(),
        config: buildConfig(type, draft),
        domains: draft.domains,
        // Only a forest domain row pins controllers; sending the field at all on any
        // other row would be asking the API to clear something it never had.
        domainController: showsControllers ? draft.domainController : undefined,
        enabled: draft.enabled && canEnable(type, draft),
        // A forest with domains is looked up domain by domain, so the selection
        // lives on each domain row, not on the forest.
        icapInstances: managesDomains ? undefined : draft.icapInstances,
        onCreated: setDirectoryId,
    });

    const goToConsent = async (event: React.MouseEvent) => {
        event.preventDefault();
        const consentUrl = draft.google.consentUrl;
        if (!req || !consentUrl || !directoryId || busy !== "") {
            return;
        }

        setBusy("consent");
        try {
            await directoriesApi.saveDirectory(req, draftToSave());
        } catch (err) {
            console.error(err);
            notification.Display({
                title: "Error",
                message: providerError(err, "Could not save this directory before sending you to Google."),
                type: "error",
            });
            setBusy("");
            return;
        }

        window.location.href = consentUrl;
    };

    const save = async (manageGroups = false) => {
        if (!req) {
            return;
        }

        const addr = normalizeLdapHost(draft.credentials.addr || "");
        if (showsDomains && addr !== "" && !sameHost(addr, discovery?.addr || "")) {
            const listed = await discoverDomains(addr);
            if (listed && !listed.failed) {
                notification.Display({
                    title: "Domains re-listed",
                    message: "The host changed, so its domains were listed again. "
                        + "Check the selection, then save.",
                    type: "information",
                });
            }
            return;
        }

        setBusy("save");
        try {
            const [saved] = await directoriesApi.saveDirectory(req, draftToSave());
            if (saved && !saved.connectionValidated) {
                const reason = saved.connectionValidationError || "The provider rejected the connection.";
                // A forest's own check never switches it off: its domains are
                // read through their own controllers, each with its own check.
                notification.Display({
                    title: managesDomains ? "Saved" : "Saved disabled",
                    message: managesDomains
                        ? `${draft.name.trim()} was saved, but its connection test failed: ${reason}`
                        : `${draft.name.trim()} was saved disabled because the connection test failed: ${reason}`,
                    type: "warning",
                });
            } else {
                notification.Display({
                    title: "Success",
                    message: `${draft.name.trim()} saved.`,
                    type: "success",
                });
            }
            const savedDirectoryId = saved?.id || directoryId;
            if (manageGroups && savedDirectoryId && canManageGroups && props.onManageGroups) {
                props.onManageGroups(savedDirectoryId);
            } else {
                props.onSaved();
            }
        } catch (err) {
            console.error(err);
            notification.Display({
                title: "Error",
                message: providerError(err, "Error saving directory integration"),
                type: "error",
            });
            setBusy("");
        }
    };

    // Where this directory stands, in one line. Which file it stands on is a detail of the
    // Authorization screen, and naming it here only put an unbreakable eighty-character
    // client id in the middle of a form that is about a name and a description.
    const googleCredentialsState = () => {
        if (draft.google.pendingCredentialsName) {
            return draft.google.hasToken
                ? "A new credentials file is waiting for consent. This directory is switched "
                    + "off until that consent is granted."
                : "A credentials file is uploaded and waiting for consent.";
        }
        if (draft.google.hasToken) {
            return "Consent granted.";
        }
        if (draft.google.hasCredential) {
            return "Credentials uploaded, consent not granted yet.";
        }
        return "No credentials uploaded yet.";
    };

    const renderGoogleCredentialsSummary = () => <>
        <Divider sx={{ marginTop: "25px" }} />
        <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing="10px"
            sx={{ marginTop: "20px" }}
        >
            <Typography
                id="google-credentials-summary"
                variant="body2"
                color="text.secondary"
                sx={{ minWidth: 0 }}
            >
                {googleCredentialsState()}
            </Typography>
            <Button
                id="google-replace-credentials-button"
                variant="outlined"
                size="medium"
                sx={{ flexShrink: 0 }}
                onClick={() => setStepIndex(steps.indexOf("consent"))}
            >
                {hasAnyCredentials ? "Replace credentials" : "Upload credentials"}
            </Button>
        </Stack>
    </>;

    const canTest = isChildDomain || requiredFieldsFilled(type, draft);

    const renderConnectionTest = () => <Box>
        <Stack direction="row" alignItems="center" spacing="12px">
            <LoadingButton
                id="test-connection-button"
                variant="outlined"
                size="medium"
                sx={{ flexShrink: 0 }}
                loading={busy === "test"}
                disabled={!canTest || (busy !== "" && busy !== "test")}
                onClick={testConnection}
            >
                Test connection
            </LoadingButton>
            <Typography variant="body2" color="text.secondary">
                {directoryId && type === "google"
                    ? "Connects and authenticates with the granted consent. The result is recorded, and a failure switches the integration off."
                    : directoryId
                    ? "Connects and authenticates. Retyped credentials aren't saved; testing the saved ones records the result, and a failure switches the integration off."
                    : "Connects and authenticates using the credentials above. Nothing is saved."}
            </Typography>
        </Stack>
        {testResult && <Alert
            id="test-connection-result"
            data-test-status={testResult.ok ? "passed" : "failed"}
            severity={testResult.ok ? "success" : "error"}
            sx={{ marginTop: "10px" }}
        >
            <Typography variant="body2">{testResult.message}</Typography>
        </Alert>}
    </Box>;

    const renderSetup = () => <Box>
        <Typography variant="body2" color="text.secondary">
            {isChildDomain
                ? "This domain inherits its credentials from the forest above it, so what is set here is its name, its description, and which of its domain controllers to read through."
                : `Give this directory a name so you can tell it apart in the table.${type === "google" ? "" : ` ${connectionHelp[type]}`}`}
        </Typography>

        <Grid container sx={{ marginTop: "20px" }}>
            <Grid item sm={12}>
                <TextField
                    id="directory-name-input"
                    label="Name"
                    fullWidth
                    variant="outlined"
                    value={draft.name}
                    onChange={(x) => patch({ name: x.target.value })}
                />
            </Grid>
            <Grid item sm={12} sx={{ marginTop: "15px" }}>
                <TextField
                    id="directory-description-input"
                    label="Description"
                    fullWidth
                    multiline
                    minRows={2}
                    variant="outlined"
                    value={draft.description}
                    onChange={(x) => patch({ description: x.target.value })}
                />
            </Grid>
        </Grid>

        {/* A forest child inherits the parent's credentials, and the API rejects credential
            edits on one, so it gets no fields to edit. */}
        {credentialFields[type].length > 0 && !isChildDomain && <>
            <Divider sx={{ marginTop: "25px" }} />
            <Grid container sx={{ marginTop: "20px" }}>
                {credentialFields[type].map((field, index) => {
                    const value = draft.credentials[field.key] || "";
                    return <Grid item sm={12} key={field.key} sx={index === 0 ? undefined : { marginTop: "15px" }}>
                        <TextField
                            id={field.id}
                            label={field.label}
                            fullWidth
                            required={field.required}
                            variant="outlined"
                            type={field.secret ? "password" : undefined}
                            value={value}
                            helperText={value === maskedSecret ? "Leave unchanged to keep the stored value." : undefined}
                            onChange={(x) => patchCredential(field.key, x.target.value)}
                            onBlur={() => patchCredentialOnBlur(field, value)}
                        />
                    </Grid>;
                })}
            </Grid>
        </>}

        {type === "google" && isEdit && renderGoogleCredentialsSummary()}

        {type !== "google" && <>
            <Divider sx={{ marginTop: "25px" }} />
            <Box sx={{ marginTop: "20px" }}>{renderConnectionTest()}</Box>
        </>}

        {domainsInSetup && <>
            <Divider sx={{ marginTop: "25px" }} />
            <Typography variant="h4" sx={{ marginTop: "20px", marginBottom: "10px" }}>Domains</Typography>
            {renderDomains()}
        </>}

        {showsControllers && <>
            <Divider sx={{ marginTop: "25px" }} />
            <Typography variant="h4" sx={{ marginTop: "20px", marginBottom: "10px" }}>Domain Controllers</Typography>
            {renderDomainControllers()}
        </>}
    </Box>;

    // The switch is the last thing set here and it is what gets written: on a directory that
    // already exists it is sent as an update, and on one that does not it rides along in the
    // create. Google is the one type that has to wait, and it waits with the switch disabled
    // rather than silently saving idle.

    const enabledHelp = () => {
        if (!canEnable(type, draft)) {
            // A file waiting for consent is the one case where this switch moved on its
            // own, so it says which action put it there and what brings it back.
            if (draft.google.pendingCredentialsName !== undefined && draft.google.hasToken) {
                return "The uploaded credentials file has not been authorized yet, so this "
                    + "directory is switched off until it is. Granting consent in the "
                    + "Authorization step starts it again, and Cancel discards the upload and "
                    + "puts it back on the file it was running.";
            }
            return "Google syncs nothing until consent has been granted. Granting it in the "
                + "Authorization step enables this directory, and you can switch it back off "
                + "from the table.";
        }
        if (isChildDomain) {
            return draft.enabled
                ? "This domain syncs while its forest is enabled."
                : "This domain stays idle, even while its forest is enabled.";
        }
        if (managesDomains) {
            if (!draft.enabled) {
                return "Every domain of this forest stays idle. Switching this on starts the "
                    + "domains that are enabled.";
            }
            return isEdit
                ? "The enabled domains of this forest sync. Switching this off stops "
                    + "all of them."
                : "Every domain you selected starts syncing as soon as this is saved.";
        }
        if (!draft.enabled) {
            return "This directory is saved but stays idle until you enable it.";
        }
        return "This directory starts syncing its groups as soon as it is saved.";
    };

    const renderEnabled = () => <Box>
        <Divider sx={{ marginTop: "25px" }} />
        <FormControlLabel
            sx={{ marginTop: "10px" }}
            control={<Switch
                id="directory-enabled-switch"
                checked={draft.enabled && canEnable(type, draft)}
                disabled={!canEnable(type, draft)}
                onChange={(x) => patch({ enabled: x.target.checked })}
            />}
            label={managesDomains ? "Enabled (all domains)" : "Enabled"}
        />
        <Typography id="directory-enabled-help" variant="body2" color="text.secondary">
            {enabledHelp()}
        </Typography>
        {!managesDomains && <ShieldInstancesSelect
            sx={{ marginTop: "20px" }}
            value={draft.icapInstances}
            onChange={(icapInstances) => patch({ icapInstances })}
        />}
    </Box>;

    const renderCredentialsFile = (id: string, label: string, name: string) => <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" component="div">{label}</Typography>
        <Typography
            id={id}
            variant="body2"
            component="div"
            sx={{ fontFamily: "monospace", fontSize: "0.78rem", overflowWrap: "anywhere" }}
        >
            {name}
        </Typography>
    </Box>;

    const renderCredentialsFiles = () => {
        if (!hasAnyCredentials) {
            return <Typography variant="body2" color="text.secondary">
                No credentials.json uploaded yet.
            </Typography>;
        }

        const replacing = draft.google.hasCredential && draft.google.pendingCredentialsName !== undefined;

        return <Stack spacing="10px" sx={{ minWidth: 0 }}>
            {draft.google.hasCredential && renderCredentialsFile(
                "google-current-credentials",
                replacing ? "Current file, kept until consent" : "Current file",
                draft.google.credentialsName || "credentials.json")}
            {draft.google.pendingCredentialsName && renderCredentialsFile(
                "google-pending-credentials",
                replacing ? "New file, waiting for consent" : "Uploaded, waiting for consent",
                draft.google.pendingCredentialsName)}
            {replacing && <Alert severity="info" id="google-replacing-credentials-alert">
                This directory is switched off while the new file waits. Granting consent below
                authorizes it and starts the directory again; Cancel discards the upload and puts
                the directory back on the current file.
            </Alert>}
        </Stack>;
    };

    const consentHelp = () => {
        if (draft.google.consentUrl) {
            return draft.google.hasCredential && draft.google.pendingCredentialsName
                ? "Sends you to Google to authorize the new file. Everything on this screen is "
                    + "saved before you go."
                : "Sends you to Google to authorize the uploaded file. Everything on this "
                    + "screen is saved before you go.";
        }
        if (draft.google.hasToken) {
            return "Consent granted. Upload a new file above to authorize a different one.";
        }
        return "Upload your credentials first.";
    };

    // Google's upload and consent round trip, which is a file and a redirect rather than typed
    // values - and strictly ordered, which is why it is its own screen.
    const renderConsent = () => <Box>
        <Typography variant="body2" color="text.secondary">{connectionHelp[type]}</Typography>

        <Grid container sx={{ marginTop: "20px" }}>
            <Grid item sm={12}>
                {googleSetup === undefined
                    ? <Alert severity="warning" id="google-redirect-uri-unknown">
                        <Typography variant="body2">
                            Could not read this server&apos;s redirect URI. Reopen this screen
                            before registering anything in the Google console.
                        </Typography>
                    </Alert>
                    : <Alert severity="info" id="google-redirect-uri-hint">
                        <Typography variant="body2">
                            The OAuth client in the Google console must list this redirect URI, and
                            the credentials file you upload below has to be the one downloaded
                            against it. It is the same for every Google directory:
                        </Typography>
                        <Stack direction="row" alignItems="center" spacing="5px" sx={{ marginTop: "5px" }}>
                            <Typography
                                variant="body2"
                                component="code"
                                sx={{ wordBreak: "break-all", fontFamily: "monospace" }}
                            >
                                {googleSetup.callbackUrl}
                            </Typography>
                            <Tooltip
                                PopperProps={{ disablePortal: true }}
                                open={callbackUrlCopied}
                                disableFocusListener
                                disableHoverListener
                                disableTouchListener
                                title="Copied!"
                            >
                                <IconButton
                                    id="copy-google-redirect-uri-button"
                                    color="primary"
                                    size="small"
                                    aria-label="Copy redirect URI"
                                    onClick={copyCallbackUrl}
                                >
                                    <ContentCopyIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                        <Typography variant="body2" sx={{ marginTop: "5px" }}>
                            If this server is reached on a different address than the one shown,
                            register that one instead - the redirect URI in the file you upload is
                            the one that gets used.
                        </Typography>
                    </Alert>}
            </Grid>
            <Grid item sm={12} sx={{ marginTop: "20px" }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing="10px">
                    <Typography variant="h4">Step 1 — Credentials</Typography>
                    <LoadingButton
                        id="upload-google-credentials-button"
                        variant="outlined"
                        size="medium"
                        component="label"
                        startIcon={<FileUploadOutlinedIcon />}
                        sx={{ flexShrink: 0 }}
                        loading={busy === "upload"}
                        disabled={busy !== "" && busy !== "upload"}
                    >
                        {hasAnyCredentials ? "Replace file" : "Upload file"}
                        <input hidden accept=".json" type="file" onChange={uploadGoogleCredentials} />
                    </LoadingButton>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ marginTop: "5px" }}>
                    Uploading attaches the file to this directory, creating it if it does not
                    exist yet - Google will only authorize a row that exists. The file takes
                    over once consent has been granted against it, and the directory stops
                    syncing until then.
                </Typography>
                <Box sx={{ marginTop: "15px" }}>{renderCredentialsFiles()}</Box>
            </Grid>
            <Grid item sm={12} sx={{ marginTop: "25px" }}>
                <Typography variant="h4">Step 2 — Consent</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ marginTop: "5px" }}>
                    {consentHelp()}
                </Typography>
                {draft.google.consentUrl && <LoadingButton
                    // A real navigation to Google, not a call of ours - goToConsent saves
                    // this screen first, because the wizard does not survive the trip. The
                    // consent flow ends at our callback, which redirects back to this page
                    // with the token stored.
                    id="grant-google-consent-button"
                    variant="contained"
                    size="medium"
                    startIcon={<OpenInNewIcon />}
                    sx={{ marginTop: "15px" }}
                    loading={busy === "consent"}
                    loadingIndicator="Saving…"
                    disabled={busy !== "" && busy !== "consent"}
                    onClick={goToConsent}
                >
                    Grant consent
                </LoadingButton>}
            </Grid>
            {draft.google.hasToken && <Grid item sm={12} sx={{ marginTop: "25px" }}>
                <Typography variant="h4" sx={{ marginBottom: "15px" }}>Step 3 — Verify</Typography>
                {renderConnectionTest()}
            </Grid>}
        </Grid>
    </Box>;

    const renderDomains = () => {
        if ((draft.credentials.addr || "").trim() === "") {
            return <Alert severity="info" id="domains-no-host-alert">
                {domainsInSetup
                    ? "Enter a host above to list this directory's domains."
                    : "Enter a host on the Setup screen to list this directory's domains."}
            </Alert>;
        }

        if (busy === "domains") {
            return <Box sx={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "20px" }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">Listing domains…</Typography>
            </Box>;
        }

        const discovered = discovery?.domains || [];
        const listed = discovered.concat(draft.domains.filter((d) => !includesDomain(discovered, d)));
        const removed = existingChildren
            .map((c) => (c as { config?: LdapDirectoryConfig }).config?.domain || "")
            .filter((d) => d !== "" && !includesDomain(draft.domains, d));

        return <Box>
            <Typography variant="body2" color="text.secondary">
                Select the domains you want to manage. Each one you select gets its own row
                beneath this directory, and users are only found in the domains you select.
            </Typography>

            {listed.length > 0 && draft.domains.length === 0 && <Alert severity="info" id="domains-none-selected-alert" sx={{ marginTop: "15px" }}>
                Select at least one domain to save this directory.
            </Alert>}

            {discovery?.failed && <Alert severity="warning" id="domains-lookup-failed-alert" sx={{ marginTop: "15px" }}>
                Could not list the domains of this host. What is selected below is what this
                directory has stored, and saving keeps it as it is.
            </Alert>}

            {removed.length > 0 && <Alert severity="warning" id="domains-removed-alert" sx={{ marginTop: "15px" }}>
                Saving removes {removed.length === 1 ? "the domain row" : `${removed.length} domain rows`} for {removed.join(", ")}.
            </Alert>}

            <Box sx={{ display: "flex", alignItems: "center", marginTop: "15px" }}>
                <Typography variant="body2" color="text.secondary">
                    {`${draft.domains.length} of ${listed.length} selected`}
                </Typography>
                <Button
                    id="rediscover-domains-button"
                    size="small"
                    sx={{ marginLeft: "auto" }}
                    disabled={busy !== ""}
                    onClick={() => discoverDomains(normalizeLdapHost(draft.credentials.addr || ""))}
                >
                    Refresh
                </Button>
            </Box>

            <Divider />

            {listed.length === 0
                ? <Typography sx={{ margin: "20px" }} variant="body1" align="center">
                    No domains found for this host.
                </Typography>
                : <Box id="domain-select-list" sx={{ display: "flex", flexDirection: "column", marginTop: "10px" }}>
                    {listed.map((domain) => <FormControlLabel
                        key={domain}
                        label={domain}
                        control={<Checkbox
                            checked={draft.domains.includes(domain)}
                            onChange={() => patch({
                                domains: draft.domains.includes(domain)
                                    ? draft.domains.filter((d) => d !== domain)
                                    : draft.domains.concat(domain),
                            })}
                        />}
                    />)}
                </Box>}
        </Box>;
    };

    const controllerPin = (dc: LdapDomainController) => controllerEndpoint(dc, draft.credentials.addr || "");

    // A controller matches the pin by either of its identities. A pin saved as a
    // host name before addresses were offered, and an address on a listing that came
    // back without one, both still name this controller - and a pin that fails to
    // match gets moved into the custom field, which is exactly the surprise to avoid.
    const controllerMatches = (dc: LdapDomainController, pin: string) => pin !== ""
        && ((dc.address !== undefined && dc.address !== "" && sameHost(dc.address, pin))
            || (dc.host !== "" && sameHost(dc.host, pin)));

    // Selecting a controller replaces the stored one: a domain row names one
    // controller and one only.
    const pinController = (dc: LdapDomainController) => {
        setCustomController(false);
        patch({ domainController: controllerPin(dc) });
    };

    // The custom option is a third state, not just an empty pin: an admin who has
    // selected it but typed nothing yet still means "I am going to name one", and
    // the discovered entries have to read as deselected while they do.
    const selectCustomController = () => {
        if (customController) {
            return;
        }
        setCustomController(true);
        patch({ domainController: "" });
    };

    const renderPinnedControllers = () => {
        // The custom field already says it's required while the admin types one in.
        if (customController || draft.domainController !== "") {
            return null;
        }
        return <Alert severity="warning" id="domain-controllers-none-note" sx={{ marginTop: "15px" }}>
            Pick a domain controller. This domain is only read through the one picked here.
        </Alert>;
    };

    // A domain that cannot be reached still has a controller configured, and that is
    // the one fact worth showing: an empty list would otherwise read as "nothing is
    // set" at exactly the moment an admin is checking what is. It stays selectable
    // checked, and the custom option below is how it gets replaced.
    const renderLastConfigured = () => {
        if (customController || draft.domainController === "") {
            return <Typography sx={{ margin: "20px" }} variant="body1" align="center">
                No domain controllers found for this domain.
            </Typography>;
        }

        return <Box id="last-configured-domain-controller" sx={{ marginTop: "10px" }}>
            <FormControlLabel
                label={<Box>
                    <Typography variant="body2">{draft.domainController}</Typography>
                    <Box sx={{ marginTop: "2px" }}>
                        <StatusLabel label="Idle - this domain did not answer" color="warning" />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                        Shown as it was saved. Saving keeps it as it is.
                    </Typography>
                </Box>}
                control={<Radio checked disabled={busy !== ""} />}
            />
        </Box>;
    };

    const renderDomainControllers = () => {
        if (busy === "controllers") {
            return <Box sx={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "20px" }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">Looking for domain controllers…</Typography>
            </Box>;
        }

        const discovered = controllers?.controllers || [];

        return <Box>
            <Typography variant="body2" color="text.secondary">
                This domain&apos;s users and groups are read only through the domain
                controller picked here. It is never looked up through DNS.
            </Typography>

            {controllersFailed && <Alert severity="warning" id="domain-controllers-failed-alert" sx={{ marginTop: "15px" }}>
                Could not list this domain&apos;s controllers. The controller below is what
                this domain has stored, and saving keeps it as it is.
            </Alert>}

            <Box sx={{ display: "flex", alignItems: "center", marginTop: "15px" }}>
                <Typography variant="body2" color="text.secondary">
                    {`${discovered.length} found${draft.domainController === "" ? "" : ", 1 selected"}`}
                </Typography>
                <Button
                    id="rediscover-domain-controllers-button"
                    size="small"
                    sx={{ marginLeft: "auto" }}
                    disabled={busy !== ""}
                    onClick={discoverDomainControllers}
                >
                    Refresh
                </Button>
            </Box>

            <Divider />

            {discovered.length === 0
                ? renderLastConfigured()
                : <Box id="domain-controller-list" sx={{ display: "flex", flexDirection: "column", marginTop: "10px" }}>
                    {discovered.map((dc) => <FormControlLabel
                        key={dc.distinguishedName || dc.name}
                        // A controller with neither an address nor a host name cannot
                        // be dialled, so there is nothing to pin - shown anyway, so it
                        // is clear why.
                        disabled={controllerPin(dc) === ""}
                        label={<Box>
                            <Typography variant="body2">{dc.host || dc.name}</Typography>
                            <Typography variant="caption" color="text.secondary" component="div">
                                {dc.host === ""
                                    ? "No DNS host name recorded, so it cannot be used."
                                    : [dc.name, dc.operatingSystem, dc.address].filter((v) => v).join(" · ")}
                            </Typography>
                        </Box>}
                        control={<Radio
                            checked={!customController && controllerMatches(dc, draft.domainController)}
                            onClick={() => pinController(dc)}
                        />}
                    />)}
                </Box>}

            <Stack direction="row" alignItems="flex-start" sx={{ marginTop: "10px", marginLeft: "-11px" }}>
                <Radio
                    id="custom-domain-controller-radio"
                    sx={{ marginTop: "-1px" }}
                    inputProps={{ "aria-label": "Use another address or host name" }}
                    checked={customController}
                    disabled={busy !== ""}
                    onClick={selectCustomController}
                />
                <TextField
                    id="domain-controller-manual-input"
                    label="Custom address or host name"
                    size="small"
                    fullWidth
                    variant="outlined"
                    value={customController ? draft.domainController : ""}
                    disabled={!customController || busy !== ""}
                    error={controllerError !== ""}
                    helperText={controllerError
                        || "protocol://host:port - for example ldap://10.1.1.101:389 or ldaps://dc01.example.com:636."}
                    onChange={(x) => patch({ domainController: x.target.value })}
                />
            </Stack>

            {renderPinnedControllers()}
        </Box>;
    };

    const saveLabel = () => {
        if (currentStep === "consent" && draft.google.consentUrl) {
            return "Save without granting consent";
        }
        return isEdit || isSinglePage ? "Save" : "Finish";
    };

    const renderStep = () => {
        switch (currentStep) {
            case "setup":
                return renderSetup();
            case "consent":
                return renderConsent();
            case "domains":
                return renderDomains();
        }
    };

    const BrandIcon = directoryIcons[type];

    return <Stack id="directory-wizard" sx={{ flex: 1, height: "100%", minWidth: 0 }}>
        <Box display="flex" sx={{ alignItems: "start", gap: "10px", minWidth: 0 }}>
            <BrandIcon sx={{ marginTop: "7px", fontSize: "26px", flexShrink: 0 }} />
            <Typography sx={{ marginTop: "5px", minWidth: 0, overflowWrap: "anywhere" }} variant="h2">
                {isEdit ? `Edit ${directory.name}` : `Add ${directoryDisplayNames[type]}`}
            </Typography>
            {directoryId && canManageGroups && props.onManageGroups && <Button
                id="manage-directory-groups-button"
                size="small"
                sx={{ marginLeft: "auto", flexShrink: 0 }}
                disabled={busy !== ""}
                onClick={async () => {
                    // Walks away from the wizard like exit does, so it owes the
                    // same teardown: a staged upload must not outlive this screen.
                    await discardStagedUpload();
                    props.onManageGroups?.(directoryId);
                }}
            >
                Manage groups
            </Button>}
            <IconButton id="exit-button" size="small" sx={{ flexShrink: 0 }} onClick={exit}>
                <CloseIcon />
            </IconButton>
        </Box>
        {isSinglePage
            ? <Box sx={{ marginTop: "20px" }} />
            : <Stepper activeStep={stepIndex} nonLinear={isEdit} sx={{ marginTop: "20px", marginBottom: "20px" }}>
                {steps.map((step, index) => <Step key={step} completed={isEdit ? false : index < stepIndex}>
                    {isEdit
                        ? <StepButton id={`step-${step}-button`} onClick={() => setStepIndex(index)}>
                            {stepDisplayNames[step]}
                        </StepButton>
                        : <StepLabel>{stepDisplayNames[step]}</StepLabel>}
                </Step>)}
            </Stepper>}
        <Box sx={{ overflowY: "auto", overflowX: "hidden", minHeight: 0, minWidth: 0, paddingBottom: "10px" }}>
            {renderStep()}
            {currentStep === enabledStep && renderEnabled()}
        </Box>

        <Divider sx={{ marginTop: "auto" }} />
        <Box display="flex" sx={{ marginTop: "10px" }}>
            {/* Both walk away from a staged upload, and both leave the directory on the
                credentials it already had. */}
            {stepIndex === 0
                ? <LoadingButton
                    id="cancel-button"
                    variant="outlined"
                    size="medium"
                    loading={busy === "discard"}
                    disabled={busy !== "" && busy !== "discard"}
                    onClick={exit}
                >
                    Cancel
                </LoadingButton>
                : <LoadingButton
                    id="back-button"
                    variant="outlined"
                    size="medium"
                    loading={busy === "discard"}
                    disabled={busy !== "" && busy !== "discard"}
                    startIcon={<ArrowBackIosNewIcon sx={{ height: "14px" }} />}
                    onClick={goBack}
                >
                    Back
                </LoadingButton>}
            {isEdit || isLastStep
                ? <Box display="flex" gap="8px" sx={{ marginLeft: "auto" }}>
                    {canManageGroups && props.onManageGroups && <LoadingButton
                        id="save-and-manage-directory-groups-button"
                        variant="outlined"
                        size="medium"
                        loading={busy === "save"}
                        disabled={!canSave || (busy !== "" && busy !== "save")}
                        onClick={() => save(true)}
                    >
                        Save and manage groups
                    </LoadingButton>}
                    <LoadingButton
                        id="save-directory-button"
                        variant="contained"
                        size="medium"
                        loading={busy === "save"}
                        disabled={!canSave || (busy !== "" && busy !== "save")}
                        onClick={() => save()}
                    >
                        {saveLabel()}
                    </LoadingButton>
                </Box>
                : <Button
                    id="next-button"
                    variant="contained"
                    size="medium"
                    sx={{ marginLeft: "auto" }}
                    disabled={!isStepComplete(currentStep, type, draft, isChildDomain)}
                    onClick={() => setStepIndex(stepIndex + 1)}
                >
                    Next
                </Button>}
        </Box>
    </Stack>;
}
